import React, { useState, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import {
  Container,
  Card,
  CardContent,
  makeStyles,
  Grid,
  TextField,
  Button,
  Modal,
  Box,
} from "@material-ui/core";
import QRCode from "qrcode";
import axios from "axios";
import CSVReader from "react-csv-reader";
import Navigation from "./Navigation";
import { CSVDownload } from "react-csv";
import { saveAs } from "file-saver";
import JSZip from "jszip";

// ── Auto-fit text ──
// Identical to the one used on the ScanQr print card: shrinks font-size
// (down to a floor) so a single line of text never overflows its
// container, and if it still can't fit at the smallest allowed size, wraps
// onto as many lines as needed instead of clipping. Kept in sync with
// ScanQr.js's FitText so the printed badge looks the same everywhere it's
// printed from.
const FitText = ({
  text,
  maxFontSize = 20,
  minFontSize = 12,
  fontWeight = 400,
  color = "#000",
  style = {},
}) => {
  const containerRef = useRef(null);
  const [renderStyle, setRenderStyle] = useState({
    fontSize: maxFontSize,
    wrapped: false,
  });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.style.fontSize = `${maxFontSize}px`;
    el.style.whiteSpace = "nowrap";
    el.style.overflow = "hidden";

    let size = maxFontSize;
    el.style.fontSize = `${size}px`;

    while (el.scrollWidth > el.clientWidth && size > minFontSize) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }

    const stillOverflowsAtFloor =
      size === minFontSize && el.scrollWidth > el.clientWidth;

    setRenderStyle({ fontSize: size, wrapped: stillOverflowsAtFloor });
  }, [text, maxFontSize, minFontSize]);

  const { fontSize, wrapped } = renderStyle;

  const overflowStyle = wrapped
    ? {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }
    : {
        whiteSpace: "nowrap",
      };

  return (
    <div
      ref={containerRef}
      style={{
        fontSize: `${fontSize}px`,
        fontWeight,
        color,
        lineHeight: 1.25,
        ...overflowStyle,
        ...style,
      }}
    >
      {text}
    </div>
  );
};

// ── Shared print card layout ──
// Same layout used on the ScanQr page's Print Preview popup and actual
// printout: Name + secondary line (Mobile No • Email) on the left
// (auto-fit / wrap via FitText), QR code on the right. `ticket` is the
// snapshot of {Firstname, MobileNo, Email, Color, Day, qrCode} captured at
// generation time for whichever card's Print button was clicked.
const PrintCardContent = ({ ticket, qrSize = 150 }) => {
  const secondaryLine = [ticket?.MobileNo, ticket?.Email]
    .filter(Boolean)
    .join(" • ");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: "16px",
      }}
    >
      <div
        style={{
          textAlign: "left",
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        <FitText
          text={ticket?.Firstname || ""}
          maxFontSize={24}
          minFontSize={14}
          fontWeight={700}
          color="#000"
        />
        <FitText
          text={secondaryLine}
          maxFontSize={16}
          minFontSize={11}
          fontWeight={400}
          color="#555"
        />
        {ticket?.Color && (
          <FitText
            text={ticket.Color}
            maxFontSize={14}
            minFontSize={10}
            fontWeight={600}
            color="#e81b23"
          />
        )}
        {(ticket?.Day || ticket?.Category) && (
          <FitText
            text={[ticket?.Day, ticket?.Category].filter(Boolean).join(" • ")}
            maxFontSize={13}
            minFontSize={10}
            fontWeight={500}
            color="#777"
          />
        )}
      </div>
      {ticket?.qrCode && (
        <img
          src={ticket.qrCode}
          alt="QR Code"
          width={qrSize}
          height={qrSize}
          style={{ display: "inline-block", flexShrink: 0 }}
        />
      )}
    </div>
  );
};

const GenerateQr = () => {
  const [links, setLinks] = useState([]);
  const [downloadFlag, setDownloadFlag] = useState(false);

  // ── Active fields ──
  const [Firstname, setFirstname] = useState("");
  const [RegistrationNumber, setRegistrationNumber] = useState("");
  const [MobileNo, setMobileNo] = useState("");
  const [Email, setEmail] = useState("");
  const [Color, setColor] = useState("");
  const [Category, setCategory] = useState("");
  const [Day, setDay] = useState("");
  const [qrCode, setQrCode] = useState("");

  const [imageUrl, setImageUrl] = useState("");
  const [uid, setUid] = useState("");

  // ── Upload progress state ──
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressText, setProgressText] = useState("");

  // ── Print preview / actual print state ──
  // `printTarget` is a snapshot { Firstname, MobileNo, Email, Color, Day, qrCode }
  // of whichever generated card's Print button was clicked, so each card
  // prints its own details rather than whatever is currently in the form
  // fields above.
  const [printOpen, setPrintOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState(null);

  const [sampleData] = useState([
    {
      Firstname: "Sample",
      RegistrationNumber: "REG12345",
      MobileNo: "9876543210",
      Email: "sample@example.com",
      Color: "Red",
      Day: "Day 1",
      Category: "VIP",
    },
  ]);

  const classes = useStyles();

  const handleSampleCsv = async () => {
    try {
      setTimeout(() => setDownloadFlag(false), 5000);
      setDownloadFlag(true);
    } catch (error) {
      console.log(error);
    }
  };

  const generateQrCode = async () => {
    try {
      const id = await saveOneTicket();
      setUid(id);
      if (id) {
        const qrCodeResponse = await QRCode.toDataURL(id);
        setImageUrl(qrCodeResponse);
        // Snapshot Firstname/MobileNo/Email/Color/Day at generation time onto
        // the link itself, so earlier cards keep showing/printing the
        // details they were generated with even after the form fields
        // change.
        setLinks((current) => [
          ...current,
          {
            qrCodeResponse,
            id,
            Firstname,
            RegistrationNumber,
            MobileNo,
            Email,
            Color,
            Day,
            Category,
          },
        ]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const generateMultipleQrCode = async (csvData) => {
    try {
      if (!csvData.length) {
        alert("Invalid File data");
        return;
      }

      setIsGenerating(true);
      setProgressText("Uploading & generating QR codes…");

      // Step 1: Send CSV to backend — backend saves users + generates QR images
      const multipleTicketsData = await saveMultipleTicket(csvData);

      if (!multipleTicketsData || !multipleTicketsData.length) {
        setIsGenerating(false);
        setProgressText("");
        return;
      }

      setProgressText(
        `Packing ${multipleTicketsData.length} QR codes into ZIP…`,
      );

      // Step 2: Build ZIP using the qrCode already stored in DB ("data:image/png;base64,...")
      const zip = new JSZip();

      for (let i = 0; i < multipleTicketsData.length; i++) {
        const record = multipleTicketsData[i];
        const storedQr = record.qrCode; // already "data:image/png;base64,..."

        if (!storedQr) {
          console.warn(
            `No qrCode stored for ${record.RegistrationNumber}, skipping`,
          );
          continue;
        }

        // Strip the data URI prefix to get raw base64
        const base64Data = storedQr.startsWith("data:image/png;base64,")
          ? storedQr.replace("data:image/png;base64,", "")
          : storedQr; // fallback if somehow already raw base64

        // Filename: registration number or firstname + index as fallback
        const filename = `${record.RegistrationNumber || record.Firstname || `qr-${i + 1}`}.png`;
        zip.file(filename, base64Data, { base64: true });
      }

      setProgressText("Saving ZIP file…");
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "qr-codes.zip");

      setIsGenerating(false);
      setProgressText("");
    } catch (error) {
      alert("Something went wrong while generating the ZIP.");
      console.log(error);
      setIsGenerating(false);
      setProgressText("");
    }
  };

  const saveMultipleTicket = async (csvData) => {
    let savedDetails;
    await axios
      .post(process.env.REACT_APP_ADMIN + "/qrCode/generateMultiple", {
        csvData,
      })
      .then((resp) => {
        alert("QR generated successfully!");
        savedDetails = resp.data;
      })
      .catch((err) => {
        alert(
          "Error generating QR codes. Please check your CSV format and try again.",
        );
        console.log(err);
      });
    return savedDetails;
  };

  const saveOneTicket = async () => {
    let ticketDetails = {
      Firstname,
      RegistrationNumber,
      MobileNo,
      Email,
      Color,
      qrCode,
      Day,
      Category,
    };
    let savedDetails;
    await axios
      .post(process.env.REACT_APP_ADMIN + "/qrCode/generateOne", ticketDetails)
      .then((resp) => {
        savedDetails = resp.data;
      });
    return savedDetails._id;
  };

  // ── Print preview / print (mirrors ScanQr.js exactly) ──
  const openPrintPreview = (link) => {
    setPrintTarget({
      Firstname: link.Firstname,
      MobileNo: link.MobileNo,
      Email: link.Email,
      Color: link.Color,
      Day: link.Day,
      Category: link.Category,
      qrCode: link.qrCodeResponse,
    });
    setPrintOpen(true);
  };

  const closePrintPreview = () => {
    setPrintOpen(false);
    setPrintTarget(null);
  };

  const handlePrintNow = () => {
    window.print();
    closePrintPreview();
  };

  return (
    <Container className={classes.conatiner}>
      <div className={classes.bgOrb1}></div>
      <div className={classes.bgOrb2}></div>
      <div className={classes.bgOrb3}></div>

      <Card className={classes.card}>
        <CardContent>
          <Navigation />

          <div className={classes.contentWrapper}>
            <Grid container spacing={4}>
              {/* ── Single QR form ── */}
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                <div className={classes.formSection}>
                  <h3 className={classes.sectionTitle}>
                    Generate Single QR Code
                  </h3>

                  <TextField
                    className={classes.field}
                    label="Registration Number"
                    variant="outlined"
                    fullWidth
                    value={RegistrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                  />
                  <TextField
                    className={classes.field}
                    label="Name"
                    variant="outlined"
                    fullWidth
                    value={Firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                  />
                  <TextField
                    className={classes.field}
                    label="Mobile No"
                    variant="outlined"
                    fullWidth
                    value={MobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                  />
                  <TextField
                    className={classes.field}
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={Email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    className={classes.field}
                    label="Color"
                    variant="outlined"
                    fullWidth
                    value={Color}
                    onChange={(e) => setColor(e.target.value)}
                  />
                  <TextField
                    className={classes.field}
                    label="Day"
                    variant="outlined"
                    fullWidth
                    value={Day}
                    onChange={(e) => setDay(e.target.value)}
                  />

                  <TextField
                    className={classes.field}
                    label="Category"
                    variant="outlined"
                    fullWidth
                    value={Category}
                    onChange={(e) => setCategory(e.target.value)}
                  />

                  <TextField
                    className={classes.field}
                    label="QR Code"
                    variant="outlined"
                    style={{ display: "none" }}
                    fullWidth
                    value={qrCode}
                    onChange={(e) => setQrCode(e.target.value)}
                  />

                  <Button
                    className={classes.btn}
                    variant="contained"
                    onClick={generateQrCode}
                  >
                    Generate QR Code
                  </Button>

                  {/* ── Generated QR results ── */}
                  {links.length > 0 && (
                    <div className={classes.qrResultsGrid}>
                      {links.map((elem, index) => {
                        const secondaryLine = [elem.MobileNo, elem.Email]
                          .filter(Boolean)
                          .join(" • ");
                        return (
                          <div key={index} className={classes.qrResultCard}>
                            <div className={classes.qrResultImageWrap}>
                              <img
                                src={elem.qrCodeResponse}
                                alt="QR Code"
                                className={classes.qrResultImage}
                              />
                            </div>
                            <p
                              className={classes.qrResultName}
                              title={elem.Firstname}
                            >
                              {elem.Firstname || "—"}
                            </p>
                            {secondaryLine && (
                              <p
                                className={classes.qrResultCompany}
                                title={secondaryLine}
                              >
                                {secondaryLine}
                              </p>
                            )}
                            {elem.Color && (
                              <p
                                className={classes.qrResultCompany}
                                title={elem.Color}
                              >
                                {elem.Color}
                              </p>
                            )}
                            {elem.Day && (
                              <p className={classes.qrResultCompany} title={elem.Day}>
                                {elem.Day}
                              </p>
                            )}
                            {elem.Category && (
                              <p className={classes.qrResultCompany} title={elem.Category}>
                                {elem.Category}
                              </p>
                            )}
                            <div className={classes.qrResultActions}>
                              <a
                                href={elem.qrCodeResponse}
                                download={`${elem.Firstname || "qr"}.png`}
                                className={classes.qrResultIconBtn}
                                title="Download"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M12 16V4M12 16L8 12M12 16L16 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M4 18V19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </a>
                              <Button
                                className={classes.btnSmall}
                                variant="contained"
                                onClick={() => openPrintPreview(elem)}
                              >
                                Print
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Grid>

              {/* ── Multiple QR upload ── */}
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                <div className={classes.uploadSection}>
                  <h3 className={classes.sectionTitle}>
                    Generate Multiple QR Codes
                  </h3>
                  <p className={classes.sectionDescription}>
                    Upload a CSV file to generate QR codes and download them as
                    a ZIP. Required columns:{" "}
                    <strong style={{ color: "#e81b23" }}>
                      Firstname, MobileNo, Email, RegistrationNumber, Color, Day, Category
                    </strong>
                  </p>

                  <div className={classes.csvReaderWrapper}>
                    <div className={classes.uploadIcon}>
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 16V8M12 8L9 11M12 8L15 11"
                          stroke="#e81b23"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20 16.7C21.2 16 22 14.6 22 13C22 10.8 20.2 9 18 9C17.7 9 17.3 9.1 17 9.1C16.3 6.7 14.1 5 11.5 5C8.4 5 6 7.4 6 10.5C6 10.7 6 10.8 6 11C4.2 11.5 3 13.1 3 15C3 17.2 4.8 19 7 19H19C19.7 19 20.4 18.8 21 18.5"
                          stroke="rgba(232,27,35,0.5)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>

                    {/* Progress indicator */}
                    {isGenerating ? (
                      <div className={classes.progressWrap}>
                        <div className={classes.spinner}></div>
                        <p className={classes.progressText}>{progressText}</p>
                      </div>
                    ) : (
                      <>
                        <p className={classes.uploadHint}>
                          Drop your CSV file here or click to browse
                        </p>
                        <CSVReader
                          parserOptions={{ header: true }}
                          onFileLoaded={(data, fileInfo) => {
                            generateMultipleQrCode(data);
                          }}
                        />
                      </>
                    )}
                  </div>

                  <div className={classes.divider}>
                    <span className={classes.dividerLine}></span>
                    <span className={classes.dividerText}>or</span>
                    <span className={classes.dividerLine}></span>
                  </div>

                  <Button
                    className={classes.btnOutline}
                    variant="outlined"
                    onClick={handleSampleCsv}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ marginRight: 8 }}
                    >
                      <path
                        d="M12 16L7 11M12 16L17 11M12 16V4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 20H21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Download Sample CSV
                  </Button>
                  {downloadFlag ? (
                    <CSVDownload data={sampleData} target="_blank" />
                  ) : null}
                </div>
              </Grid>
            </Grid>
          </div>
        </CardContent>
      </Card>

      {/* ── Print Preview popup — identical styling/behavior to ScanQr.js ── */}
      <Modal open={printOpen} onClose={closePrintPreview}>
        <Box className={classes.printModal}>
          <button className={classes.printCloseBtn} onClick={closePrintPreview}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <p className={classes.printTitle}>Print Preview</p>

          <div
            style={{
              background: "#fff",
              color: "#000",
              borderRadius: "10px",
              padding: "15vh 20px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <PrintCardContent ticket={printTarget} qrSize={120} />
          </div>

          <Button
            className={classes.btn}
            variant="contained"
            color="primary"
            style={{ width: "100%" }}
            onClick={handlePrintNow}
          >
            Print
          </Button>
        </Box>
      </Modal>

      {/*
        ── ACTUAL print target, rendered via portal directly onto document.body ──
        Same approach as ScanQr.js: stays laid out at all times (sized to
        its real 3.5in x 5in card dimensions) but kept invisible/off-screen
        via visibility + position, so FitText always measures a real,
        non-zero box instead of the 0-width it would get from display:none.
      */}
      {printOpen &&
        ReactDOM.createPortal(
          <div
            className="qr-print-target"
            style={{
              background: "#fff",
              color: "#000",
              width: "3.5in",
              height: "5in",
              boxSizing: "border-box",
              padding: "12.5% 15px 20px 30px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <PrintCardContent ticket={printTarget} qrSize={150} />
          </div>,
          document.body,
        )}

      {printOpen && (
        <style>{`
          .qr-print-target {
            visibility: hidden;
            position: fixed;
            top: 0;
            left: 0;
          }
          @media print {
            @page { size: 3.5in 5in; margin: 0; }
            html, body {
              height: 100%;
              margin: 0;
            }
            body * { visibility: hidden; }
            .qr-print-target, .qr-print-target * { visibility: visible; }
          }
        `}</style>
      )}
    </Container>
  );
};

const useStyles = makeStyles((theme) => ({
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(28px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes slideDown": {
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes orbFloat1": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "33%": { transform: "translate(60px, -80px) scale(1.15)" },
    "66%": { transform: "translate(-40px, 40px) scale(0.9)" },
  },
  "@keyframes orbFloat2": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "33%": { transform: "translate(-70px, 50px) scale(1.1)" },
    "66%": { transform: "translate(50px, -60px) scale(0.95)" },
  },
  "@keyframes orbFloat3": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(40px, 60px) scale(1.2)" },
  },
  "@keyframes uploadPulse": {
    "0%, 100%": { opacity: 0.6, transform: "scale(1)" },
    "50%": { opacity: 1, transform: "scale(1.08)" },
  },
  "@keyframes qrPopIn": {
    from: { opacity: 0, transform: "scale(0.85) translateY(16px)" },
    to: { opacity: 1, transform: "scale(1) translateY(0)" },
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  // ── Print popup: pops open FROM the exact center of the screen ──
  "@keyframes popFromCenter": {
    "0%": { opacity: 0, transform: "translate(-50%, -50%) scale(0.25)" },
    "60%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.04)" },
    "100%": { opacity: 1, transform: "translate(-50%, -50%) scale(1)" },
  },

  conatiner: {
    background:
      "linear-gradient(160deg, #0a0a0f 0%, #12121a 40%, #0d0d15 100%)",
    height: "100vh",
    width: "100vw",
    maxWidth: "100vw",
    position: "relative",
    padding: "0 !important",
    overflow: "hidden",
  },

  bgOrb1: {
    position: "fixed",
    top: "-15%",
    left: "-10%",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(232,27,35,0.18) 0%, rgba(232,27,35,0.04) 50%, transparent 70%)",
    animation: "$orbFloat1 12s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(8px)",
  },
  bgOrb2: {
    position: "fixed",
    bottom: "-20%",
    right: "-10%",
    width: 700,
    height: 700,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(180,10,18,0.14) 0%, rgba(120,0,10,0.05) 50%, transparent 70%)",
    animation: "$orbFloat2 16s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(12px)",
  },
  bgOrb3: {
    position: "fixed",
    top: "40%",
    left: "30%",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,80,80,0.06) 0%, transparent 70%)",
    animation: "$orbFloat3 20s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(20px)",
  },

  card: {
    background: "rgba(14,14,20,0.88)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow:
      "0 8px 64px 0 rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)",
    height: "99.6%",
    margin: "0 !important",
    padding: "0 !important",
    overflowY: "scroll",
    position: "relative",
    zIndex: 1,
    animation: "$fadeIn 0.7s cubic-bezier(0.22,1,0.36,1)",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255,255,255,0.03)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(180deg,#e81b23 0%,#7b0008 100%)",
      borderRadius: "10px",
    },
  },

  contentWrapper: {
    padding: theme.spacing(4),
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1)",
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
  },

  formSection: {
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    padding: theme.spacing(4),
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    transition: "box-shadow 0.4s ease, border-color 0.4s ease",
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.3)",
      boxShadow:
        "0 8px 40px rgba(232,27,35,0.15), inset 0 1px 0 rgba(255,255,255,0.07)",
    },
  },

  uploadSection: {
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    padding: theme.spacing(4),
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    transition: "box-shadow 0.4s ease, border-color 0.4s ease",
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s backwards",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.3)",
      boxShadow:
        "0 8px 40px rgba(232,27,35,0.15), inset 0 1px 0 rgba(255,255,255,0.07)",
    },
  },

  sectionTitle: {
    color: "#f0f0f5",
    fontSize: "1.4rem",
    fontWeight: 700,
    marginTop: 0,
    marginBottom: theme.spacing(3),
    letterSpacing: "0.03em",
    textShadow: "0 0 20px rgba(232,27,35,0.4)",
  },

  sectionDescription: {
    color: "rgba(180,180,200,0.55)",
    fontSize: "0.9rem",
    marginTop: 0,
    marginBottom: theme.spacing(3),
    lineHeight: 1.7,
  },

  field: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.07)",
      borderRadius: "10px",
      color: "#f0f0f5",
      transition: "all 0.3s ease",
      "& input": { color: "#f0f0f5" },
      "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.10)",
        "& fieldset": { borderColor: "rgba(232,27,35,0.5)" },
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(232,27,35,0.06)",
        boxShadow: "0 0 0 3px rgba(232,27,35,0.12)",
        "& fieldset": { borderColor: "#e81b23", borderWidth: "2px" },
      },
    },
    "& .MuiInputLabel-root": { color: "rgba(180,180,200,0.6)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#e81b23", fontWeight: 600 },
  },

  btn: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    backgroundColor: "#e81b23",
    color: "#fff",
    fontWeight: 700,
    padding: "12px 32px",
    borderRadius: "10px",
    textTransform: "none",
    fontSize: "0.95rem",
    letterSpacing: "0.02em",
    boxShadow: "0 4px 16px rgba(232,27,35,0.35)",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      backgroundColor: "#c8121a",
      transform: "translateY(-3px) scale(1.02)",
      boxShadow: "0 8px 28px rgba(232,27,35,0.55)",
    },
  },

  // ── Small pill variant of `btn`, used inside each result card ──
  btnSmall: {
    backgroundColor: "#e81b23",
    color: "#fff",
    fontWeight: 700,
    padding: "6px 18px",
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "0.8rem",
    letterSpacing: "0.02em",
    boxShadow: "0 3px 12px rgba(232,27,35,0.35)",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      backgroundColor: "#c8121a",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(232,27,35,0.5)",
    },
  },

  btnOutline: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    backgroundColor: "transparent",
    color: "#e81b23",
    fontWeight: 700,
    padding: "11px 28px",
    borderRadius: "10px",
    border: "1.5px solid rgba(232,27,35,0.5)",
    textTransform: "none",
    fontSize: "0.95rem",
    letterSpacing: "0.02em",
    display: "flex",
    alignItems: "center",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      backgroundColor: "rgba(232,27,35,0.1)",
      borderColor: "#e81b23",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(232,27,35,0.25)",
    },
  },

  csvReaderWrapper: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(4, 3),
    background: "rgba(232,27,35,0.04)",
    borderRadius: "14px",
    border: "2px dashed rgba(232,27,35,0.3)",
    textAlign: "center",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    minHeight: "160px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      borderColor: "#e81b23",
      background: "rgba(232,27,35,0.08)",
      boxShadow: "0 0 0 4px rgba(232,27,35,0.06)",
    },
    "& input[type='file']": {
      cursor: "pointer",
      color: "rgba(200,200,210,0.7)",
    },
    "& label": { color: "rgba(200,200,210,0.7)" },
  },

  uploadIcon: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(1.5),
    animation: "$uploadPulse 3s ease-in-out infinite",
  },

  uploadHint: {
    color: "rgba(180,180,200,0.5)",
    fontSize: "0.82rem",
    margin: "0 0 12px 0",
    letterSpacing: "0.02em",
  },

  // ── Progress indicator ──
  progressWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
    padding: theme.spacing(2, 0),
  },

  spinner: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "3px solid rgba(232,27,35,0.2)",
    borderTopColor: "#e81b23",
    animation: "$spin 0.8s linear infinite",
  },

  progressText: {
    color: "rgba(200,200,215,0.7)",
    fontSize: "0.85rem",
    margin: 0,
    letterSpacing: "0.02em",
  },

  divider: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(3, 0),
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    background: "rgba(255,255,255,0.08)",
    borderRadius: "1px",
  },
  dividerText: {
    color: "rgba(180,180,200,0.35)",
    fontSize: "0.78rem",
    fontWeight: 500,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },

  // ── Generated QR result cards ──
  qrResultsGrid: {
    marginTop: theme.spacing(3),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },

  qrResultCard: {
    width: "100%",
    background: "linear-gradient(160deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: theme.spacing(2.5, 2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    animation: "$qrPopIn 0.4s cubic-bezier(0.22,1,0.36,1) backwards",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.35)",
      transform: "translateY(-4px)",
      boxShadow: "0 10px 32px rgba(232,27,35,0.18)",
    },
  },

  qrResultImageWrap: {
    background: "#fff",
    borderRadius: "10px",
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  qrResultImage: {
    width: "120px",
    height: "120px",
    display: "block",
  },

  qrResultName: {
    color: "#f0f0f5",
    fontWeight: 700,
    fontSize: "0.95rem",
    margin: "6px 0 0 0",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "center",
  },

  qrResultCompany: {
    color: "rgba(200,200,210,0.6)",
    fontSize: "0.8rem",
    margin: 0,
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "center",
  },

  qrResultActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },

  qrResultIconBtn: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "rgba(210,210,225,0.75)",
    transition: "all 0.2s ease",
    textDecoration: "none",
    "&:hover": {
      background: "rgba(232,27,35,0.15)",
      borderColor: "rgba(232,27,35,0.4)",
      color: "#fff",
    },
  },

  // ── Print Preview modal — identical styling to ScanQr.js ──
  printModal: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transformOrigin: "center center",
    animation: "$popFromCenter 0.4s cubic-bezier(0.22,1,0.36,1) forwards",
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    borderRadius: "20px",
    boxShadow:
      "0 24px 64px rgba(0,0,0,0.7),0 0 0 1px rgba(255,255,255,0.08),inset 0 1px 0 rgba(255,255,255,0.06)",
    border: "1px solid rgba(232,27,35,0.2)",
    padding: "1.75rem",
    width: "min(360px, 90vw)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
  },

  printCloseBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "rgba(210,210,225,0.6)",
    transition: "all 0.2s ease",
    padding: 0,
    "&:hover": {
      background: "rgba(232,27,35,0.18)",
      borderColor: "rgba(232,27,35,0.4)",
      color: "#fff",
      transform: "rotate(90deg)",
    },
  },

  printTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "1rem",
    margin: 0,
    letterSpacing: "0.04em",
  },
}));

export default GenerateQr;